import React, { useState, useEffect, useRef } from 'react';
import './FilterPanel.css';

const DAYS = [
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'this_weekend', label: 'This Wknd' },
  { id: 'Monday', label: 'Mon' },
  { id: 'Tuesday', label: 'Tue' },
  { id: 'Wednesday', label: 'Wed' },
  { id: 'Thursday', label: 'Thu' },
  { id: 'Friday', label: 'Fri' },
  { id: 'Saturday', label: 'Sat' },
  { id: 'Sunday', label: 'Sun' },
];

const WHAT_OPTIONS = [
  { id: 'happy_hour', label: 'Happy Hour' },
  { id: 'specials', label: 'Specials' },
  { id: 'Live Music', label: 'Live Music' },
  { id: 'Trivia', label: 'Trivia' },
  { id: 'Comedy', label: 'Comedy' },
  { id: 'Karaoke', label: 'Karaoke' },
  { id: 'Bingo', label: 'Bingo' },
  { id: 'DJ', label: 'DJ' },
  { id: 'Sports', label: 'Sports' },
  { id: 'Open Mic', label: 'Open Mic' },
];

// Default locations (fallback) - now passed via props from market config
const DEFAULT_LOCATIONS = [
  { id: 'St. Charles', label: 'St. Charles' },
  { id: 'Geneva', label: 'Geneva' },
  { id: 'Batavia', label: 'Batavia' },
  { id: 'Campton Hills', label: 'Campton Hills' },
];

// Resolve "today", "tomorrow", "this_weekend" to actual day names
function resolveDayFilter(dayId) {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const now = new Date();

  if (dayId === 'today') {
    return [days[now.getDay()]];
  }
  if (dayId === 'tomorrow') {
    return [days[(now.getDay() + 1) % 7]];
  }
  if (dayId === 'this_weekend') {
    return ['Friday', 'Saturday', 'Sunday'];
  }
  // Regular day name
  return [dayId];
}

// Resolve WHEN filters to an actual date range for one-time event filtering.
// resolveDayFilter returns day NAMES (good for recurring events), but one-time
// events need specific date boundaries — "This Wknd" means THIS Fri-Sun, not
// every future Friday/Saturday/Sunday.
export function resolveWhenDateRange(whenFilters) {
  if (whenFilters.length === 0) return null;

  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  let minDate = null;
  let maxDate = null;

  for (const dayId of whenFilters) {
    if (dayId === 'today') {
      const start = new Date(today);
      const end = new Date(today);
      end.setHours(23, 59, 59, 999);
      if (!minDate || start < minDate) minDate = start;
      if (!maxDate || end > maxDate) maxDate = end;
    } else if (dayId === 'tomorrow') {
      const tmrw = new Date(today);
      tmrw.setDate(tmrw.getDate() + 1);
      const end = new Date(tmrw);
      end.setHours(23, 59, 59, 999);
      if (!minDate || tmrw < minDate) minDate = new Date(tmrw);
      if (!maxDate || end > maxDate) maxDate = end;
    } else if (dayId === 'this_weekend') {
      const dayOfWeek = today.getDay(); // 0=Sun, 5=Fri, 6=Sat
      const friday = new Date(today);
      if (dayOfWeek === 0) {
        // Sunday — "this weekend" includes today
        friday.setDate(friday.getDate() - 2);
      } else {
        const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
        friday.setDate(friday.getDate() + daysUntilFriday);
      }
      const sunday = new Date(friday);
      sunday.setDate(sunday.getDate() + 2);
      sunday.setHours(23, 59, 59, 999);
      const start = friday < today ? new Date(today) : friday;
      if (!minDate || start < minDate) minDate = start;
      if (!maxDate || sunday > maxDate) maxDate = sunday;
    } else {
      // Specific day name — find the next occurrence
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const targetDay = dayNames.indexOf(dayId);
      if (targetDay >= 0) {
        const next = new Date(today);
        const daysUntil = (targetDay - today.getDay() + 7) % 7;
        next.setDate(next.getDate() + (daysUntil === 0 ? 0 : daysUntil));
        const end = new Date(next);
        end.setHours(23, 59, 59, 999);
        if (!minDate || next < minDate) minDate = new Date(next);
        if (!maxDate || end > maxDate) maxDate = end;
      }
    }
  }

  return minDate && maxDate ? { minDate, maxDate } : null;
}

// Get human-readable label for a filter
function getFilterLabel(type, id, locations = DEFAULT_LOCATIONS) {
  if (type === 'when') {
    return DAYS.find(d => d.id === id)?.label || id;
  }
  if (type === 'what') {
    return WHAT_OPTIONS.find(w => w.id === id)?.label || id;
  }
  if (type === 'where') {
    return locations.find(l => l.id === id)?.label || id;
  }
  return id;
}

function FilterPanel({ isOpen, onClose, filters, onApplyFilters, calculateResultCount, locations }) {
  // Use passed locations or fallback to defaults
  const LOCATIONS = locations || DEFAULT_LOCATIONS;
  const [localFilters, setLocalFilters] = useState(filters);
  const [liveResultCount, setLiveResultCount] = useState(null);
  const panelRef = useRef(null);
  const backdropRef = useRef(null);

  // Sync local state when panel opens
  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, filters]);

  // Update live result count whenever local filters change
  useEffect(() => {
    if (isOpen && calculateResultCount) {
      setLiveResultCount(calculateResultCount(localFilters));
    }
  }, [isOpen, localFilters, calculateResultCount]);

  const toggleChip = (section, id) => {
    setLocalFilters(prev => {
      const current = prev[section] || [];
      const updated = current.includes(id)
        ? current.filter(item => item !== id)
        : [...current, id];
      return { ...prev, [section]: updated };
    });
  };

  const toggleNow = () => {
    setLocalFilters(prev => ({ ...prev, happeningNow: !prev.happeningNow }));
  };

  const clearAll = () => {
    setLocalFilters({ when: [], what: [], where: LOCATIONS.map(l => l.id), happeningNow: false });
  };

  const handleApply = () => {
    onApplyFilters(localFilters);
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === backdropRef.current) {
      onClose();
    }
  };

  const totalActive = (localFilters.when?.length || 0) +
    (localFilters.what?.length || 0) +
    (localFilters.where?.length || 0) +
    (localFilters.happeningNow ? 1 : 0);

  if (!isOpen) return null;

  return (
    <div className="filter-panel-backdrop" ref={backdropRef} onClick={handleBackdropClick}>
      <div className="filter-panel" ref={panelRef}>
        <div className="filter-panel-handle" />

        <div className="filter-panel-header">
          <h2>Filters</h2>
          <button className="filter-panel-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="filter-panel-body">
          {/* WHEN */}
          <div className="filter-section">
            <h3 className="filter-section-title">When</h3>
            <div className="filter-chips">
              {DAYS.map(day => (
                <button
                  key={day.id}
                  className={`filter-chip ${localFilters.when?.includes(day.id) ? 'active' : ''}`}
                  onClick={() => toggleChip('when', day.id)}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-divider" />

          {/* WHAT */}
          <div className="filter-section">
            <h3 className="filter-section-title">What</h3>
            <div className="filter-chips">
              {WHAT_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  className={`filter-chip ${localFilters.what?.includes(opt.id) ? 'active' : ''}`}
                  onClick={() => toggleChip('what', opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-divider" />

          {/* WHERE */}
          <div className="filter-section">
            <h3 className="filter-section-title">Where</h3>
            <div className="filter-chips">
              {LOCATIONS.map(loc => (
                <button
                  key={loc.id}
                  className={`filter-chip ${localFilters.where?.includes(loc.id) ? 'active' : ''}`}
                  onClick={() => toggleChip('where', loc.id)}
                >
                  {loc.label}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-divider" />

          {/* HAPPENING NOW */}
          <div className="filter-section">
            <div className="filter-now-toggle">
              <span className="filter-now-label">Happening Now</span>
              <button
                className={`filter-toggle ${localFilters.happeningNow ? 'active' : ''}`}
                onClick={toggleNow}
              >
                <span className="filter-toggle-knob" />
              </button>
            </div>
          </div>
        </div>

        <div className="filter-panel-footer">
          <button className="filter-apply-btn" onClick={handleApply}>
            Show Results{liveResultCount !== null ? ` (${liveResultCount})` : ''}
          </button>
          {totalActive > 0 && (
            <button className="filter-clear-btn" onClick={clearAll}>
              Clear All
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export { resolveDayFilter, getFilterLabel, WHAT_OPTIONS };
export default FilterPanel;
